import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiGet } from "@/lib/api";
import type { EventDto, PostDto, PublicSettings } from "@/types";
import { PostCard } from "@/components/content/PostCard";
import { Sidebar } from "@/components/layout/Sidebar";
import { HighlightBar } from "@/components/widgets/HighlightBar";
import { HeroSlider } from "@/components/widgets/HeroSlider";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

/**
 * Home page.
 *
 * Updates from the second round of feedback:
 *  - The static "Wir von hier / Vereine / Termine" row was removed
 *    (it duplicated menu items and could not be edited; the spec
 *    intended that band to be reserved for `HighlightBar` instead).
 *  - "Aktuelles" is now split into the three project categories
 *    (Informationen / Veranstaltungen / Ereignisse).
 *  - Hero kicker / title / tagline read their colour from the
 *    `theme.hero_*` tokens, allowing admins to change them
 *    independently from the brand primary.
 *  - The hero image source is taken from `settings.hero.image_url`
 *    (editable in Admin -> Startseite).
 */
export function HomePage() {
  const dlocale = de;
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => apiGet<PublicSettings>("/public/settings"),
    staleTime: 10 * 60_000,
  });
  const { data: posts } = useQuery({
    queryKey: ["home-posts", "de"],
    queryFn: () => apiGet<{ content: PostDto[] }>("/public/posts?locale=" + "de" + "&size=24"),
  });
  const { data: events } = useQuery({
    queryKey: ["home-events", "de"],
    queryFn: () => apiGet<EventDto[]>("/public/events/upcoming?locale=" + "de" + "&limit=5"),
  });

  const siteName = settings?.site_name ?? "Falkenrehde";
  const tagline = settings?.site_tagline ?? "zu Hause im Havelland";
  const heroImage = settings?.hero?.image_url ?? "/Dorfkirche-mit-Falke.jpg";
  const heroKickerColor   = settings?.theme?.hero_kicker_color   ?? "rgba(255,255,255,0.80)";
  const heroTitleColor    = settings?.theme?.hero_title_color    ?? "#ffffff";
  const heroTaglineColor  = settings?.theme?.hero_tagline_color  ?? "rgba(255,255,255,0.92)";

  const byCategory = bucketByCategory(posts?.content ?? []);

  return (
    <>
      <Helmet>
        <title>{siteName} - {tagline}</title>
        <meta name="description" content={siteName + " - aktuelle Nachrichten, Termine und Informationen aus dem Ortsteil " + siteName + "."} />
      </Helmet>

      <section className="relative overflow-hidden">
        <HeroSlider
          images={
            (settings?.hero?.image_urls && settings.hero.image_urls.length > 0
              ? settings.hero.image_urls
              : [heroImage])
          }
          alt="Dorfkirche Falkenrehde mit Falke"
          intervalSec={settings?.hero?.slide_seconds ?? 6}
        />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="max-w-content mx-auto px-4 lg:px-6 py-24 lg:py-36 relative">
          <p className="uppercase tracking-[0.25em] text-xs" style={{ color: heroKickerColor }}>
            Ortsteil der Stadt Ketzin/Havel
          </p>
          <h1 className="font-serif text-5xl lg:text-7xl font-bold mt-3 drop-shadow-md"
              style={{ color: heroTitleColor }}>
            {siteName}
          </h1>
          <p className="mt-5 text-lg lg:text-2xl max-w-2xl font-serif italic"
             style={{ color: heroTaglineColor }}>
            {tagline}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/aktuelles" className="bg-white text-primary px-6 py-3 rounded-md font-semibold hover:bg-white/95 shadow-card transition">
              {"Aktuelles aus dem Dorf"}
            </Link>
            <Link to="/kalender" className="border border-white/50 text-white px-6 py-3 rounded-md font-semibold hover:bg-white/10 backdrop-blur-sm transition">
              {"Nächste Termine"}
            </Link>
          </div>
        </div>
      </section>

      {/* Hervorgehobene Mitteilung - renders nothing when empty (spec 2.3). */}
      <HighlightBar />

      <div className="max-w-content mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-14">
          <CategorySection
            title="Informationen"
            color="var(--color-cat-information)"
            posts={byCategory.information}
            href="/aktuelles?cat=information"
          />
          <CategorySection
            title="Veranstaltungen"
            color="var(--color-cat-veranstaltung)"
            posts={byCategory.veranstaltung}
            href="/aktuelles?cat=veranstaltung"
          />
          <CategorySection
            title="Ereignisse"
            color="var(--color-cat-ereignis)"
            posts={byCategory.ereignis}
            href="/aktuelles?cat=ereignis"
          />

          <section>
            <div className="flex items-end justify-between mb-5">
              <h2 className="text-2xl lg:text-3xl font-bold font-serif text-primary">{"Nächste Termine"}</h2>
              <Link to="/kalender" className="text-primary text-sm hover:underline">Zum Kalender</Link>
            </div>
            <ul className="divide-y divide-border bg-bg border border-border rounded-lg overflow-hidden">
              {events?.length === 0 && <li className="p-6 text-text-muted italic">{"Keine Termine in diesem Zeitraum."}</li>}
              {events?.map((e) => (
                <li key={e.id + "-" + e.startAt} className="p-4 flex items-center gap-5 hover:bg-bg-muted card-hover">
                  <div className="text-center min-w-[64px] py-2 rounded-md bg-primary-soft">
                    <div className="text-xs uppercase text-primary">{format(parseISO(e.startAt), "MMM", { locale: dlocale })}</div>
                    <div className="text-2xl font-bold text-primary leading-none font-serif">{format(parseISO(e.startAt), "d", { locale: dlocale })}</div>
                    <div className="text-[10px] uppercase text-primary/80 mt-0.5">{format(parseISO(e.startAt), "EEE", { locale: dlocale })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={"/kalender/" + e.slug} className="font-medium hover:text-primary block truncate">{e.title}</Link>
                    <div className="text-sm text-text-muted truncate">
                      {!e.allDay && format(parseISO(e.startAt), "HH:mm", { locale: dlocale }) + " Uhr"}
                      {e.location && (e.allDay ? "" : " - ") + e.location}
                    </div>
                  </div>
                  <span className="hidden sm:inline text-xs text-primary uppercase tracking-wide">{e.category}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <aside className="space-y-6">
          <Sidebar />
        </aside>
      </div>
    </>
  );
}

function CategorySection({
  title, color, posts, href,
}: {
  title: string; color: string; posts: PostDto[]; href: string;
}) {
  if (posts.length === 0) return null;
  return (
    <section>
      <div className="flex items-end justify-between mb-5">
        <h2 className="text-2xl lg:text-3xl font-bold font-serif text-primary flex items-center gap-3">
          <span aria-hidden className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
          {title}
        </h2>
        <Link to={href} className="text-primary text-sm hover:underline">Alle ansehen</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.slice(0, 3).map((p) => <PostCard key={p.id} post={p} />)}
      </div>
    </section>
  );
}

function bucketByCategory(posts: PostDto[]) {
  const out: Record<"information" | "veranstaltung" | "ereignis", PostDto[]> =
    { information: [], veranstaltung: [], ereignis: [] };
  for (const p of posts) {
    const cat = (p.category ?? "").toLowerCase();
    if (cat === "information"   || cat === "informationen")  out.information.push(p);
    else if (cat === "veranstaltung" || cat === "veranstaltungen") out.veranstaltung.push(p);
    else if (cat === "ereignis" || cat === "ereignisse") out.ereignis.push(p);
    else out.information.push(p); // unknown -> default bucket so editors see them
  }
  return out;
}
